import {Component, computed, inject, input, signal} from '@angular/core';
import {TranslocoDirective} from '@jsverse/transloco';
import {
  ArrowRightFromLineIcon,
  ArrowRightToLineIcon,
  GraduationCapIcon,
  LucideAngularModule,
  MapPinIcon,
  SparklesIcon,
  SquareUserRoundIcon,
  TriangleAlertIcon
} from 'lucide-angular';
import {ActivityDTO} from '@domain/activity/activity.dto';
import {ActivityScheduleUtil} from '@domain/activity/schedule/activity-schedule.util';
import {VenuePipe} from '@domain/venue/pipe/venue.pipe';
import {ScheduleAgePipe} from '@domain/activity/schedule/pipe/schedule-age.pipe';
import {ScheduleLevelPipe} from '@domain/activity/schedule/pipe/schedule-level.pipe';
import {ActivityScheduleDTO} from '@domain/activity/schedule/schedule.dto';
import {
  ScheduleLevelIconComponent
} from '@domain/activity/schedule/component/schedule-level-icon/schedule-level-icon.component';
import {ScheduleGenderPipe} from '@domain/activity/schedule/pipe/schedule-gender.pipe';
import {PersonGenderIconComponent} from '@domain/common/component/person-gender-icon/person-gender-icon.component';
import {GeoUtil} from '@domain/common/util/geo.util';
import {MapComponent} from '@shared/map/map.component';
import {
  ScheduleTimeComponent
} from '@domain/activity/schedule/component/schedule-display/schedule-time/schedule-time.component';

@Component({
  selector: 'app-schedule-display',
  imports: [
    TranslocoDirective,
    LucideAngularModule,
    ScheduleLevelIconComponent,
    PersonGenderIconComponent,
    MapComponent,
    ScheduleTimeComponent
  ],
  providers: [
    ScheduleAgePipe,
    ScheduleLevelPipe,
    ScheduleGenderPipe,
    VenuePipe
  ],
  templateUrl: './schedule-display.component.html',
  styleUrl: './schedule-display.component.scss'
})
export class ScheduleDisplayComponent {
  public readonly activity = input.required<ActivityDTO>();
  public readonly schedule = input.required<ActivityScheduleDTO>();
  public readonly showDistance = input(true);
  public readonly showAddress = input(true);

  public readonly coordinates = computed(() => GeoUtil.toDecimal(this.schedule()?.venue?.coordinates));
  public readonly showMap = signal(false);

  public readonly icons = {
    map: MapPinIcon,
    supervisor: GraduationCapIcon,
    since: ArrowRightFromLineIcon,
    until: ArrowRightToLineIcon,
    age: SquareUserRoundIcon,
    warn: TriangleAlertIcon,
    improve: SparklesIcon
  };

  public readonly isFinished = computed(() => ActivityScheduleUtil.isFinished(this.schedule()));

  public readonly venueText = computed(() => {
    const distance = this.distance();
    let venueStr = this.showDistance() && distance ? distance + ' - ' : '';
    venueStr += this.activityVenuePipe.transform(this.schedule().venue, {showAddress: this.showAddress()});
    return venueStr;
  });

  public readonly genderString = computed(() => this.scheduleGenderPipe.transform(this.schedule()?.gender));
  public readonly ageString = computed(() => this.scheduleAgePipe.formatAge(this.schedule()));
  public readonly levelString = computed(() => this.scheduleLevelPipe.format(this.schedule(), this.activity()));

  private readonly distance = computed(() => {
    const distance = this.schedule()?.venue?.distance;
    if (!distance) {
      return null;
    }
    if (distance > 1000) {
      return (distance / 1000).toFixed(1) + ' km';
    }
    return '< 1 km'
  });

  private readonly activityVenuePipe = inject(VenuePipe);
  private readonly scheduleAgePipe = inject(ScheduleAgePipe);
  private readonly scheduleLevelPipe = inject(ScheduleLevelPipe);
  private readonly scheduleGenderPipe = inject(ScheduleGenderPipe);

  public toggleShowMap(): void {
    this.showMap.update(val => !val);
  }
}
